import { createContext, useContext, useState, type ReactNode } from 'react';
import { formatInZone } from '@workforce/shared';
import { useAuth } from './auth';

/**
 * How times are shown:
 *  - "employee": each record in the time zone of the employee it belongs to
 *  - "company":  everything in the company time zone (admin setting)
 *  - "mine":     everything in the viewer's own time zone
 */
export type TzMode = 'employee' | 'company' | 'mine';

interface TzState {
  mode: TzMode;
  setMode: (m: TzMode) => void;
  /** Resolve the zone to display a record whose owner is in `employeeZone`. */
  zoneFor: (employeeZone?: string | null) => string;
  fmt: (instant: string | Date | null | undefined, employeeZone?: string | null, format?: string) => string;
  viewerZone: string;
  companyZone: string;
}

const Ctx = createContext<TzState | null>(null);
const KEY = 'wf.tzmode';

function readMode(): TzMode {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'employee' || v === 'company' || v === 'mine') return v;
  } catch {
    /* storage unavailable */
  }
  return 'employee';
}

export function TimeZoneProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [mode, setModeState] = useState<TzMode>(readMode);
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const viewerZone = user?.employee?.timezone ?? browserZone;
  const companyZone = user?.companyTimezone ?? 'UTC';

  const zoneFor = (employeeZone?: string | null) => {
    if (mode === 'company') return companyZone;
    if (mode === 'mine') return viewerZone;
    return employeeZone ?? viewerZone;
  };
  const setMode = (m: TzMode) => {
    setModeState(m);
    try {
      localStorage.setItem(KEY, m);
    } catch {
      /* ignore */
    }
  };
  return (
    <Ctx.Provider
      value={{
        mode,
        setMode,
        zoneFor,
        fmt: (instant, employeeZone, format) => formatInZone(instant, zoneFor(employeeZone), format),
        viewerZone,
        companyZone,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useTz(): TzState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTz outside TimeZoneProvider');
  return v;
}
